import SalesReturns, { useSalesReturns } from "../RetourClient/SalesReturns";
﻿import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
} from "reactstrap";

import {
    FaBarcode,
    FaFilter,
    FaPlus,
    FaSearch,
    FaSyncAlt,
    FaFilePdf,
    FaEdit,
    FaCalendarAlt,
    FaUser,
    FaCheckCircle,
} from "react-icons/fa";

import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";

import POSHeader from "./POSHeader";
import "./BonComptoireList.css";

import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { clientActions } from "../../sagas/clientSlice";
import { commercialActions } from "../../sagas/commercialSlice";
import { productActions } from "../../sagas/productSlice";

import { formatPrice, formatTime } from "../../helpers/utility";
import { CBL_PAGE, CPMT_PRODUCTS_PAGE, CPMT_RECEPTION_PAGE, CPMT_STOCK_PAGE, PAY_PAGE, POS_PAGE, RETOUR_FORM_NEW, RETOUR_PAGE } from "../../routes/routeConstants";
import POSKeypadModal from "./POSKeypadModal";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { buildEscPosReceipt } from '../../helpers/printUtility';
import { printPdfInHiddenIframe } from "../../helpers/printFrame";
import { shiftActions } from "../../sagas/shiftSlice";
const BonComptoireList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [selectedBonLivraison, setSelectedBonLivraison] = useState(null);
    const { list = [], loading } = useSelector((state) => state.deliveryNotes);
    const clients = useSelector((state) => state.clients.clients || []);
    const { businessName, commercialId, email, address, telephone, fullName, shiftId, pointOfSaleId } = useSelector((state) => state.auth);
    const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);
    const [pdfModal, setPdfModal] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const { summary, list: shifts, currentShift, loading: shiftLoading, error: shiftError } = useSelector(
        (state) => state.shift
    );
    const searchInputRef = useRef(null);

    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const pad = (n) => n.toString().padStart(2, "0");
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const [activeTab, setActiveTab] = useState("VENTES");

    // ✅ POS Filters
    const [filters, setFilters] = useState({
        query: "",
        clientId: null,
        commercialId: commercialId,
        dateFrom: formatDate(todayDate),
        dateTo: formatDate(todayDate),
    });

    const [filterModal, setFilterModal] = useState(false);
    const [clientModal, setClientModal] = useState(false);
    const latestFiltersRef = useRef(filters);

    const selectedClient = useMemo(() => clients.find((c) => c.id === filters.clientId), [clients, filters.clientId]);

    // ✅ Fetch initial data
    useEffect(() => {
        dispatch(productActions.findAllUnites());
        dispatch(clientActions.search({ query: "" }));
        dispatch(shiftActions.fetchList({
            commercialId: commercialId,
            pointOfSaleId: pointOfSaleId,
        }))
    }, [dispatch]);

    // ✅ List refresh
    const refrechBLs = useCallback(() => {
        console.log(filters.dateFrom)
        dispatch(deliveryActions.listComptoire({ ...filters }));
        dispatch(shiftActions.fetchSummary({ shiftId: currentShift?.id }));

    }, [dispatch, filters.clientId, filters.dateFrom, filters.dateTo, currentShift]);

    useEffect(() => {
        refrechBLs();
    }, [refrechBLs]);

    useEffect(() => {
        if (currentShift?.id) {
            setFilters((prev) => ({
                ...prev,
                shiftId: prev.shiftId ?? currentShift.id,
            }));
        }
    }, [currentShift?.id]);

    useEffect(() => {
        latestFiltersRef.current = filters;
    }, [filters]);


    // ✅ Debounced search
    const debouncedSearch = useRef(
        debounce((q) => {
            dispatch(deliveryActions.listComptoire({ ...latestFiltersRef.current, query: q }));
        }, 500)
    ).current;

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const clearFilters = () => {
        setFilters({
            query: "",
            clientId: null,
            shiftId: currentShift?.id ?? null,
            dateFrom: formatDate(todayDate),
            dateTo: formatDate(todayDate),
        });
    };

    // ✅ POS badge
    const getStatusBadge = (status) => {
        if (status === "EN_PREPARATION") return <Badge color="warning">{t("delivery.enpreparation")}</Badge>;
        if (status === "EN_COURS") return <Badge color="secondary">{t("delivery.encours")}</Badge>;
        if (status === "LIVRE") return <Badge color="success">{t("delivery.livre")}</Badge>;
        if (status === "RETOUR") return <Badge color="danger">{t("delivery.retourne")}</Badge>;
        return <Badge color="light" className="text-dark">{status}</Badge>;
    };

    const totalTTC = useMemo(() => list.reduce((sum, bl) => sum + (bl.montantTotalTTC || 0), 0), [list]);
    const totalPaye = useMemo(() => list.reduce((sum, bl) => sum + (bl.montantPaye || 0), 0), [list]);
    const totalEncaisseAvecTimbre = Number(summary?.netPaymentsAmount ?? summary?.totalPaymentsAmount ?? 0);
    const pendingStatuses = useMemo(() => new Set(["EN_PREPARATION", "EN_ATTENTE"]), []);
    const sortedList = useMemo(
        () => [...list].sort((a, b) => Number(pendingStatuses.has(b.status)) - Number(pendingStatuses.has(a.status))),
        [list, pendingStatuses]
    );

    const handleGeneratePdf = async (ticketData , format = "THERMAL") => {
        try {
              const measureDoc = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: [80, 350],
              });
              const estimatedAddressHeight = address
                ? measureDoc.splitTextToSize(address, 72).slice(0, 3).length * 3.3
                : 0;
              const estimatedItemsHeight = (ticketData?.lignes || []).reduce((height, item) => {
                const nameLines = measureDoc.splitTextToSize(item.articleNom || "-", 38);
                return height + 3.8 + Math.max(nameLines.length - 1, 0) * 3.4;
              }, 0);
	              const payment = getReceiptPaymentSummary(ticketData);
	              const receiptHeight = Math.ceil(
	                265 +
	                estimatedAddressHeight +
	                (telephone ? 3.5 : 0) +
	                estimatedItemsHeight +
	                (payment.stampDuty > 0 ? 4.2 : 0) +
	                (payment.change > 0 ? 4.2 : 0)
	              );
	              const thermalPageHeight = Math.min(Math.max(receiptHeight, 230), 280);
	              const doc = new jsPDF({
	                orientation: "portrait",
	                unit: "mm",
	                format: [80, thermalPageHeight],
	              });
        
              const pageWidth = 80;
              const margin = 4;
              const contentWidth = pageWidth - margin * 2;
	              const right = pageWidth - margin;
	              let y = 7;
	              const pageHeight = doc.internal.pageSize.getHeight();
	              const bottomMargin = 8;
	
	              const ensureSpace = (height = 0) => {
	                if (y + height <= pageHeight - bottomMargin) return;
	                doc.addPage([pageWidth, pageHeight], "portrait");
	                y = 7;
	              };
	        
	              const drawRule = (bold = false) => {
	                ensureSpace(bold ? 4 : 3.5);
	                doc.setDrawColor(0);
	                doc.setLineWidth(bold ? 0.35 : 0.12);
	                doc.line(margin, y, right, y);
                y += bold ? 3.6 : 3;
              };
	        
	              const drawRow = (label, value, { bold = false, size = 9.5 } = {}) => {
	                ensureSpace(size >= 11 ? 5.5 : 4.7);
	                doc.setFont("helvetica", bold ? "bold" : "normal");
	                doc.setFontSize(size);
	                doc.text(label, margin, y);
                doc.text(formatPrice(value, false), right, y, { align: "right" });
                y += size >= 11 ? 5 : 4.2;
              };
	        
	              const drawInfo = (label, value) => {
	                ensureSpace(4.2);
	                doc.setFont("helvetica", "normal");
	                doc.setFontSize(8.2);
	                doc.text(`${label}:`, margin, y);
                doc.text(String(value || "-"), margin + 15, y, { maxWidth: contentWidth - 15 });
                y += 3.8;
              };
        
              const totalQuantity = (ticketData?.lignes || []).reduce(
                (sum, item) => sum + Number(item.quantiteLivree || 0),
                0
              );
        
              doc.setFont("helvetica", "bold");
              doc.setFontSize(15);
              doc.text(businessName || "MATJARPOS", pageWidth / 2, y, { align: "center" });
              y += 5.5;
        
              doc.setFont("helvetica", "normal");
              doc.setFontSize(8);
              if (address) {
                const lines = doc.splitTextToSize(address, contentWidth);
                lines.slice(0, 3).forEach((line) => {
                  doc.text(line, pageWidth / 2, y, { align: "center" });
                  y += 3.3;
                });
              }
              if (telephone) {
                doc.text(`Tel: ${telephone}`, pageWidth / 2, y, { align: "center" });
                y += 3.5;
              }
        
              drawRule(true);
        
              doc.setFont("helvetica", "bold");
              doc.setFontSize(11);
              doc.text("TICKET DE CAISSE", pageWidth / 2, y, { align: "center" });
              y += 5;
        
              drawInfo("Ticket", ticketData?.numero);
              drawInfo("Date", ticketData?.createdAt || ticketData?.dateOperation);
              drawInfo("Client", ticketData?.clientNom || "Passager");
              //if (ticketData?.createdBy) drawInfo("Caissier", ticketData.createdBy);
        
              drawRule();
        
              doc.setFont("helvetica", "bold");
              doc.setFontSize(8.5);
              doc.text("DESIGNATION", margin, y);
              doc.text("QTE", 47, y, { align: "right" });
              doc.text("P.U.", 59, y, { align: "right" });
              doc.text("TOTAL", right, y, { align: "right" });
              y += 3.5;
              drawRule();
        
              (ticketData?.lignes || []).forEach((item) => {
                const qte = Number(item.quantiteLivree || 0);
	                const pu = Number(item.prixUnitaireTTC || 0);
	                const total = Number(item.montantTTC || qte * pu);
	                const nameLines = doc.splitTextToSize(item.articleNom || "-", 38);
	                ensureSpace(3.8 + Math.max(nameLines.length - 1, 0) * 3.4);
	        
	                doc.setFont("helvetica", "bold");
	                doc.setFontSize(8.5);
                doc.text(nameLines[0], margin, y);
                doc.text(String(qte), 47, y, { align: "right" });
                doc.text(formatPrice(pu, false), 59, y, { align: "right" });
                doc.text(formatPrice(total, false), right, y, { align: "right" });
                y += 3.8;
        
                if (nameLines.length > 1) {
                  doc.setFont("helvetica", "normal");
                  doc.setFontSize(8);
                  nameLines.slice(1).forEach((line) => {
                    doc.text(line, margin + 2, y);
                    y += 3.4;
                  });
	                }
	              });
	        
	              ensureSpace(90);
	              drawRule();
	              doc.setFont("helvetica", "normal");
	              doc.setFontSize(8.5);
              doc.text(`${ticketData?.lignes?.length || 0} article(s)`, margin, y);
              doc.text(`${totalQuantity} piece(s)`, right, y, { align: "right" });
              y += 4.2;
              drawRule(true);
        
              drawRow("TOTAL TTC", ticketData?.montantTotalTTC || 0, { bold: true, size: 11 });
              if (payment.stampDuty > 0) {
	                drawRow("Droit de timbre 0,25%", payment.stampDuty);
	              }
	        
	              ensureSpace(10);
	              doc.setFillColor(0, 0, 0);
	              doc.rect(margin, y - 0.5, contentWidth, 7, "F");
	              doc.setTextColor(255, 255, 255);
              doc.setFont("helvetica", "bold");
              doc.setFontSize(11);
              doc.text("NET A PAYER", margin + 2, y + 4.2);
              doc.text(formatPrice(payment.totalDue, false), right - 2, y + 4.2, { align: "right" });
	              doc.setTextColor(0, 0, 0);
	              y += 9.5;
	        
	              ensureSpace(34);
	              doc.setFont("helvetica", "bold");
	              doc.setFontSize(9.5);
	              doc.text("PAIEMENT", margin, y);
              y += 4.2;
              drawInfo("Mode", getModeReglementLabel(ticketData?.modeReglement));
              drawRow("Especes", payment.cash);
              drawRow("Carte", payment.card);
              drawRow("Montant paye", payment.paid, { bold: true });
              if (payment.change > 0) {
                drawRow("Monnaie", payment.change, { bold: true });
	              }
	        
	              ensureSpace(18);
	              drawRule();
	              doc.setFont("helvetica", "normal");
	              doc.setFontSize(8.5);
              doc.text("Merci de votre confiance", pageWidth / 2, y, { align: "center" });
              y += 4;
              doc.text("A bientot", pageWidth / 2, y, { align: "center" });
              y += 4;
              doc.text("cima.ma / @cimamarket.ma", pageWidth / 2, y, { align: "center" });
        
	              printPdfInHiddenIframe(doc.output("bloburl"));
            } catch (e) {
              console.error(e);
            }
    };

    const printEscPosReceipt = data => {
        const escpos = buildEscPosReceipt(data);
        const encoded = encodeURIComponent(escpos);

        window.location.href = `rawbt://print?text=${encoded}`;
    };
    const getModeReglementLabel = (mode) => {
        switch (mode) {
            case 'ESPECES': return 'Espèces';
            case 'CARTE_BANCAIRE': return 'Carte bancaire';
            case 'CHEQUE': return 'Chèque';
            case 'VIREMENT': return 'Virement';
            case 'TRAITE': return 'Traite';
            case 'EN_COMPTE': return 'En compte';
            default: return mode || '-';
        }
    };

    const getReceiptPaymentSummary = (data) => {
        const total = Number(data?.montantTotalTTC || 0);
        const cash = Number(data?.montantKeptCash ?? data?.montantPayeCash ?? data?.montantPayeEspece ?? 0);
        const card = Number(data?.montantPayeCarte || 0);
        const stampDuty = Number(data?.montantDroitTimbre || 0);
        const totalDue = total + stampDuty;
        const explicitPaid = Number(data?.montantPaye || 0);
        const computedPaid = cash + card;
        const paid = explicitPaid > 0 ? Math.max(explicitPaid, computedPaid) : computedPaid;
        const change = Math.max(paid - totalDue, 0);
        return { total, cash, card, paid, change, stampDuty, totalDue };
    };

    const generateA5PDF = async (receiptData) => {
        try {
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a5',
            });

            const pageWidth = 148;
            const margin = 10;
            let y = 15;

            // ===== HEADER =====
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.text(businessName || 'MATJARPOS', pageWidth / 2, y, { align: 'center' });
            y += 8;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.text('REÇU DE VENTE', pageWidth / 2, y, { align: 'center' });
            y += 6;

            doc.setFont('helvetica', 'normal');
            doc.text(`N° : ${receiptData?.numero}`, margin, y);
            doc.text(`Date : ${receiptData?.dateOperation}`, pageWidth - margin, y, { align: 'right' });
            y += 6;

            doc.text(`Client : ${receiptData?.clientNom || 'Passager'}`, margin, y);
            y += 6;

            doc.line(margin, y, pageWidth - margin, y);
            y += 6;

            // ===== ARTICLES =====
            doc.setFont('helvetica', 'bold');
            doc.text('Article', margin, y);
            doc.text('Qté', pageWidth - 60, y);
            doc.text('Total', pageWidth - margin, y, { align: 'right' });
            y += 4;

            doc.line(margin, y, pageWidth - margin, y);
            y += 4;

            doc.setFont('helvetica', 'normal');
            receiptData?.lignes?.forEach(item => {
                const article = doc.splitTextToSize(item.articleNom, 70);
                doc.text(article, margin, y);
                doc.text(String(item.quantiteLivree), pageWidth - 60, y);
                doc.text(formatPrice(item.montantTTC, false), pageWidth - margin, y, { align: 'right' });
                y += article.length * 4 + 2;
            });

            doc.line(margin, y, pageWidth - margin, y);
            y += 6;

            const payment = getReceiptPaymentSummary(receiptData);

            // ===== TOTAL =====
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('TOTAL TTC', margin, y);
            doc.text(formatPrice(receiptData?.montantTotalTTC, false), pageWidth - margin, y, { align: 'right' });
            y += 8;

            if (payment.stampDuty > 0) {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.text('Droit de timbre 0,25% :', margin, y);
                doc.text(formatPrice(payment.stampDuty, false), pageWidth - margin, y, { align: 'right' });
                y += 6;

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.text('TOTAL A PAYER', margin, y);
                doc.text(formatPrice(payment.totalDue, false), pageWidth - margin, y, { align: 'right' });
                y += 8;
            }

            doc.line(margin, y, pageWidth - margin, y);
            y += 6;

            // ===== PAIEMENT =====
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);

            const modeLabel = getModeReglementLabel(receiptData?.modeReglement);
            doc.text(`Mode de paiement : ${modeLabel}`, margin, y);
            y += 6;

            doc.text("Montant payé :", margin, y);
            doc.text(formatPrice(payment.paid, false), pageWidth - margin, y, { align: 'right' });
            y += 6;
            doc.text("Espèces :", margin, y);
            doc.text(formatPrice(payment.cash, false), pageWidth - margin, y, { align: 'right' });
            y += 6;

            doc.text("Carte :", margin, y);
            doc.text(formatPrice(payment.card, false), pageWidth - margin, y, { align: 'right' });
            y += 6;
            if (payment.change > 0) {
                doc.text('Monnaie :', margin, y);
                doc.text(
                    formatPrice(payment.change, false),
                    pageWidth - margin,
                    y,
                    { align: 'right' }
                );
                y += 6;
            }

            doc.line(margin, y, pageWidth - margin, y);
            y += 8;

            // ===== SOLDE CLIENT =====
            // if (!receiptData?.isComptoir && receiptData?.clientTotalDebit && receiptData?.clientTotalCredit) {
            //     doc.setFont('helvetica', 'bold');
            //     doc.setFontSize(10);
            //     doc.text('SOLDE CLIENT', margin, y);

            //     doc.setFont('helvetica', 'normal');
            //     doc.text(
            //         formatClientSolde(
            //             receiptData.clientTotalDebit,
            //             receiptData.clientTotalCredit
            //         ),
            //         pageWidth - margin,
            //         y,
            //         { align: 'right' }
            //     );

            //     y += 8;
            // }

            // ===== FOOTER =====
            doc.setFontSize(9);
            doc.text('Merci de votre confiance', pageWidth / 2, y, { align: 'center' });
            y += 4;
            doc.text('cima.ma / @cimamarket.ma', pageWidth / 2, y, { align: 'center' });
            y += 6;

            // ===== ADDRESS AT BOTTOM =====
            if (address) {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.text(address, pageWidth / 2, 148 - 10, { align: 'center' });
            }

            printPdfInHiddenIframe(URL.createObjectURL(doc.output('blob')));
        } catch (e) {
            console.error(e);
        }
    };


    const handleEdit = (bl) => {
        navigate(POS_PAGE, {
            state: {
                editMode: true,
                deliveryNote: bl
            }
        });
    };

    const handleLivrerBL = (bl) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('bonLivraison.confirmLivrer'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: bl.id,
                data: { ...bl, status: 'LIVRE' },
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('bonLivraison.updatedSuccess'),
                    });

                    refrechBLs()

                },
            };
            dispatch(deliveryActions.update(payload));
        });
    }



    // ✅ UI
    const salesReturns = useSalesReturns(list);

    return (
        <Container fluid className="pos-list-page">
            <POSHeader
                businessName={businessName || "MATJARPOS"}
                fullName={fullName}
                commercialId={commercialId}
                pointOfSaleId={pointOfSaleId}
                activeTab={activeTab}
                onChangeTab={(tab) => {
                    setActiveTab(tab);
                    if (tab === "VENTES") navigate(POS_PAGE);
                    if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
                    if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
                    if (tab === "AVANCE") navigate(PAY_PAGE);
                    if (tab === "RETOURS") navigate(RETOUR_FORM_NEW);
                    if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
                }}
            />

            {summary && (
                <Card className="pos-shift-summary shadow-sm border-0">
                    <CardBody className="d-flex align-items-center justify-content-between flex-wrap gap-3">

                        {/* LEFT – Shift identity */}
                        <div className="shift-meta">
                            <div className="shift-title">
                                <strong>{summary.commercialName || fullName}</strong>
                                <Badge
                                    color={summary.status === "OUVERT" ? "success" : "secondary"}
                                    className="ms-2"
                                >
                                    {summary.status}
                                </Badge>
                            </div>

                            <div className="shift-sub text-muted">
                                Ouvert à : <strong>{formatTime(summary.openingTime)}</strong>
                            </div>
                        </div>

                        {/* RIGHT – KPIs */}
                        <div className="shift-kpis">
                            <div className="shift-kpi">
                                <small>Ventes</small>
                                <strong>{summary?.numberOfSales ?? 0}</strong>
                            </div>

                            <div className="shift-kpi">
                                <small>Ventes nettes</small>
                                <strong>{formatPrice(summary?.netSalesAmount ?? summary?.totalSalesAmount ?? 0, false)}</strong>
                            </div>

                            <div className="shift-kpi">
                                <small>Encaissé net</small>
                                <strong className="text-success">
                                    {formatPrice(totalEncaisseAvecTimbre, false)}
                                </strong>
                                <em>Droit timbre inclus</em>
                            </div>

                            <div className="shift-kpi">
                                <small>Cash net</small>
                                <strong className="text-success">
                                    {formatPrice(summary?.netCashPayments ?? summary?.totalCashPayments ?? 0, false)}
                                </strong>
                            </div>

                            <div className="shift-kpi">
                                <small>Carte nette</small>
                                <strong className="text-primary">
                                    {formatPrice(summary?.netCardPayments ?? summary?.totalCardPayments ?? 0, false)}
                                </strong>
                            </div>

                        </div>

                    </CardBody>
                </Card>
            )}

            {/* ✅ POS TOP BAR */}
            <div className="pos-topbar shadow-sm">
                <div className="pos-search">
                    <FaSearch className="icon" />

                    <Input
                        innerRef={searchInputRef}
                        value={filters.query}
                        placeholder={t("bonLivraison.searchPlaceholder")}
                        readOnly
                        inputMode="none"
                        onKeyDown={(e) => e.preventDefault()}
                        className="pos-search-input pos-nokeyboard"
                        onClick={() => setSearchKeypadOpen(true)}
                    />

                    <FaBarcode className="barcode" />
                </div>


                <div className="pos-actions">
                    <div className="pos-action-stack">
                        <Label className="pos-label pos-label-spacer">.</Label>
                        <Button
                            className="pos-btn pos-btn-light"
                            color="light"
                            onClick={() => setClientModal(true)}
                        >
                            Client : <strong className="ms-2">{selectedClient ? (selectedClient.typeClient === "PARTICULIER" ? `${selectedClient.nom} ${selectedClient.prenom || ""}` : selectedClient.entreprise) : "Tous"}</strong>
                        </Button>
                    </div>
                    <div className="pos-date-range">
                        <FormGroup className="mb-0 pos-date-field">
                            <Label className="pos-label">{t("common.from", "Du")}</Label>
                            <Input
                                type="date"
                                className="form-control pos-input"
                                value={filters.dateFrom || ""}
                                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value || null }))}
                            />
                        </FormGroup>
                        <FormGroup className="mb-0 pos-date-field">
                            <Label className="pos-label">{t("common.to", "Au")}</Label>
                            <Input
                                type="date"
                                className="form-control pos-input"
                                value={filters.dateTo || ""}
                                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value || null }))}
                            />
                        </FormGroup>
                    </div>

                    <div className="pos-action-stack">
                        <Label className="pos-label pos-label-spacer">.</Label>
                        <Button className="pos-btn pos-btn-primary" onClick={() => navigate(POS_PAGE)}>
                            <FaPlus /> <span className="d-none d-md-inline ms-2">{t("delivery.new")}</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* ✅ LIST */}
            <Card className="pos-list-card shadow-sm border-0">
                <CardBody className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t("common.loading")}</div>
                        </div>
                    ) : list.length === 0 ? (
                        <div className="text-center py-5 text-muted">{t("delivery.noBL")}</div>
                    ) : (
                        <div className="pos-list">
                            {sortedList.map((bl) => (
                                <div key={bl.id} className="pos-list-row">
                                    <div className="left">
                                        <div className="top">
                                            <strong className="numero">{bl.numero}</strong>
                                            <SalesReturns document={bl} state={salesReturns} />
                                            {getStatusBadge(bl.status)}
                                        </div>

                                        <div className="meta">
                                            <span><FaUser className="me-1" /> {bl.clientNom || "Passager"}</span>
                                            <span><FaCalendarAlt className="me-1" /> {bl.createdAt}</span>
                                        </div>

                                        <div className="amounts">
                                            <span className="ttc">{formatPrice(bl.montantTotalTTC, false)} DH</span>
                                            <span className="paye">{formatPrice(bl.montantPaye, false)} DH</span>
                                        </div>
                                    </div>

                                    <div className="right">
                                        <Button
                                            className="pos-icon-btn pos-pdf"
                                            onClick={() => {
                                                handleGeneratePdf(bl, "THERMAL");

                                            }}
                                            title="Imprimer"
                                        >
                                            <FaFilePdf />
                                        </Button>

                                        <Button
                                            className="pos-icon-btn pos-edit"
                                            onClick={() => {
                                                handleEdit(bl)
                                            }}
                                            title={t("commande.edit")}
                                        >
                                            <FaEdit />
                                        </Button>
                                        {bl.status === 'EN_PREPARATION' && bl.modeReglement && <Button
                                            color="success"
                                            title={t('bonLivraison.livrer')}
                                            onClick={() => handleLivrerBL(bl)}
                                            className="pos-icon-btn pos-confirm"
                                            size="sm"
                                        >
                                            <FaCheckCircle />
                                        </Button>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* ✅ FILTER MODAL */}
            <Modal isOpen={filterModal} toggle={() => setFilterModal(false)} centered size="lg">
                <ModalHeader toggle={() => setFilterModal(false)}>Filtres</ModalHeader>
                <ModalBody>
                    <Row className="g-3">
                        <Col md={6}>
                            <Button
                                className="w-100 pos-filter-btn"
                                color="light"
                                onClick={() => setClientModal(true)}
                            >
                                Client : <strong className="ms-2">{selectedClient ? (selectedClient.typeClient === "PARTICULIER" ? `${selectedClient.nom} ${selectedClient.prenom || ""}` : selectedClient.entreprise) : "Tous"}</strong>
                            </Button>
                        </Col>

                        <Col md={6}>
                            <FormGroup className="mb-0">
                                <Label className="pos-label">Date début</Label>
                                <Input
                                    type="date"
                                    className="form-control pos-input"
                                    value={filters.dateFrom}
                                    onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6}>
                            <FormGroup className="mb-0">
                                <Label className="pos-label">Date fin</Label>
                                <Input
                                    type="date"
                                    className="form-control pos-input"
                                    value={filters.dateTo}
                                    onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>

                <ModalFooter className="d-flex justify-content-between">
                    <Button color="danger" outline onClick={clearFilters}>
                        Réinitialiser
                    </Button>
                    <Button color="primary" onClick={() => setFilterModal(false)}>
                        Appliquer
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ✅ CLIENT MODAL */}
            <Modal isOpen={clientModal} toggle={() => setClientModal(false)} centered size="lg">
                <ModalHeader toggle={() => setClientModal(false)}>Choisir Client</ModalHeader>
                <ModalBody className="p-0">
                    <div className="pos-picker">
                        <Button
                            color={!filters.clientId ? "primary" : "light"}
                            className="pos-picker-item"
                            onClick={() => {
                                setFilters((f) => ({ ...f, clientId: null }));
                                setClientModal(false);
                            }}
                        >
                            Tous
                        </Button>

                        {clients.map((c) => {
                            const label =
                                c.typeClient === "PARTICULIER"
                                    ? `${c.nom} ${c.prenom || ""}`
                                    : c.entreprise;

                            return (
                                <Button
                                    key={c.id}
                                    color={filters.clientId === c.id ? "primary" : "light"}
                                    className="pos-picker-item"
                                    onClick={() => {
                                        setFilters((f) => ({ ...f, clientId: c.id }));
                                        setClientModal(false);
                                    }}
                                >
                                    {label}
                                </Button>
                            );
                        })}
                    </div>
                </ModalBody>
            </Modal>

            {/* ✅ SHIFT MODAL */}

            <POSKeypadModal
                isOpen={searchKeypadOpen}
                toggle={() => setSearchKeypadOpen(false)}
                value={filters.query}
                onChange={(val) => {
                    const v = (val || "").toUpperCase();
                    setFilters((f) => ({ ...f, query: v }));
                }}
                onSearch={() => {
                    debouncedSearch(filters.query);
                    setSearchKeypadOpen(false);
                }}
                title={`Recherche BL`}
            />



        </Container>
    );
};

export default BonComptoireList;
